import { c as _c } from "react/compiler-runtime";
import { firebaseApp } from '@netpurpose/api';
import { useLoggedIn } from '@netpurpose/core';
import { logger } from '@netpurpose/utils';
import { type Analytics, getAnalytics, isSupported, logEvent, setUserId } from 'firebase/analytics';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import React, { type FC, type ReactNode, useEffect, useState } from 'react';
import { config } from '#services/config';
import { AnalyticsEventName, type AnalyticsEventParams } from '#types/analytics';
const analyticsEnabled = config.firebase.analyticsEnabled && config.firebase.measurementId && config.firebase.appId;

// Privacy-first browsers such as Firefox will block analytics in such a way
// that we need to track whether tracking is supported at all and avoid throwing
// an error below if not, otherwise the whole application won't load.
let trackingSupported: null | boolean = null;
export const defaultContext = null;
export const AnalyticsContext = React.createContext<(Analytics & {
  isInitialised: boolean;
}) | null>(defaultContext);
export const AnalyticsProvider: FC<{
  children: ReactNode;
}> = t0 => {
  const $ = _c(11);
  const {
    children
  } = t0;
  const router = useRouter();
  const {
    user
  } = useLoggedIn();
  const [tracking, setTracking] = useState(defaultContext);
  const [isInitialised, setIsInitialised] = useState(false);
  const userId = user?.id;
  let t1;
  let t2;
  if ($[0] !== router.events || $[1] !== tracking || $[2] !== userId) {
    t1 = () => {
      if (analyticsEnabled) {
        isSupported().then(isSupp => {
          if (!isSupp) {
            logger.warn("Analytics not supported");
            trackingSupported = false;
            setIsInitialised(true);
            return;
          }
          const analytics = getAnalytics(firebaseApp);
          setTracking(analytics);
          trackingSupported = true;
          setIsInitialised(true);
          if (userId) {
            setUserId(analytics, userId);
          }
        });
        const handleRouteChange = url => {
          if (!tracking) {
            return;
          }
          logEvent(tracking, AnalyticsEventName.PageView, {
            page_location: url,
            page_title: document?.title
          });
        };
        router.events.on("routeChangeStart", handleRouteChange);
        return () => {
          router.events.off("routeChangeStart", handleRouteChange);
        };
      } else {
        setIsInitialised(true);
      }
    };
    t2 = [router.events, tracking, userId];
    $[0] = router.events;
    $[1] = tracking;
    $[2] = userId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== isInitialised || $[6] !== tracking) {
    t3 = tracking ? {
      ...tracking,
      isInitialised
    } as Analytics & {
      isInitialised: boolean;
    } : null;
    $[5] = isInitialised;
    $[6] = tracking;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const contextValue = t3;
  let t4;
  if ($[8] !== children || $[9] !== contextValue) {
    t4 = <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
    $[8] = children;
    $[9] = contextValue;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export const useAnalytics = () => {
  const $ = _c(5);
  const context = React.useContext(AnalyticsContext);
  if (analyticsEnabled && trackingSupported && context?.isInitialised && context.app === null) {
    throw new Error("useAnalytics must be used within a AnalyticsProvider");
  }
  let t0;
  if ($[0] !== context) {
    t0 = (eventName, eventParams) => {
      posthog.capture(eventName, eventParams);
      if (context) {
        logEvent(context, eventName, eventParams);
      }
    };
    $[0] = context;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== context || $[3] !== t0) {
    t1 = {
      logEvent: t0,
      app: context
    };
    $[2] = context;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export type AnalyticsContextType = ReturnType<typeof useAnalytics>;